/**
 * Text Tooltip Utilities - Helper functions for text rendering with tooltip support
 * 
 * Handles smart text truncation based on column configuration and responsive tooltip display
 * 
 * @author CTS - Ford PM UI Team
 */

import React from 'react';
import { Tooltip } from '@mui/material';
import { Info,ElectricalServices as ElectricalServicesIcon } from '@mui/icons-material';
import { AppLabels, UIConfig } from '../constant/constant';
import { DashboardCopyableDisplay } from '../pages/dashBoard/Dashboard.styles';

import {
  PlugDrawerCriticalDeviceDisplay,
} from '../pages/dashBoard/PlugDrawer.styles';

/**
 * Private helper function to check if text should show tooltip
 * @private
 */
const _shouldShowTooltip = (text, columnKey) => {
  if (!text) return false;
  const lengthLimit = UIConfig.columnTextLimits[columnKey];
  return lengthLimit && text.length > lengthLimit;
};

/**
 * Private helper function to get truncated text
 * @private
 */
const _getTruncatedText = (text, columnKey) => {
  const truncatedLimit = UIConfig.truncatedTextLimits[columnKey];
  return truncatedLimit ? `${text.substring(0, truncatedLimit)}...` : text;
};
/**
 * Renders text with tooltip support when text exceeds configured limits
 * Shows tooltip with full text and info icon when truncation occurs
 * 
 * @param {string} text - Text to display
 * @param {string} columnKey - Column key to get length limit from UIConfig
 * @param {string} fallbackText - Text to show when input is null/undefined (default: AppLabels.notApplicable)
 * @returns {React.ReactNode} Rendered text with optional tooltip
 * 
 * @example
 * renderTextWithTooltip("Very long device name", "deviceName")
 * // Returns: Truncated text with tooltip
 */
export const renderTextWithTooltip = (text, columnKey, fallbackText = AppLabels.notApplicable) => {
  const textValue = text || fallbackText;
  const lengthLimit = UIConfig.columnTextLimits[columnKey];
  const truncatedLimit = UIConfig.truncatedTextLimits[columnKey];
  
  // If no limits are configured for this column, return text as-is
  if (!lengthLimit || !truncatedLimit) {
    return textValue;
  }

  const shouldShowTooltip = _shouldShowTooltip(textValue, columnKey);
  const displayText = shouldShowTooltip 
    ? _getTruncatedText(textValue, columnKey)
    : textValue;
  
  if (shouldShowTooltip) {
    return (
      <DashboardCopyableDisplay>
        <Tooltip 
          title={textValue} 
          arrow 
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: '400px',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }
            }
          }}
        >
          <span 
            style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '4px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: UIConfig.tooltipSettings.maxWidth,
              // fontWeight: columnKey === 'deviceName' ? 'bold' : 'normal'
            }}
          >
            {displayText}
            <Info 
              style={{ 
                fontSize: UIConfig.tooltipSettings.iconSize, 
                color: UIConfig.tooltipSettings.iconColor, 
                flexShrink: 0
              }} 
            />
          </span>
        </Tooltip>
      </DashboardCopyableDisplay>
    );
  }
  return textValue;
  // return columnKey === 'deviceName' ? <strong>{textValue}</strong> : textValue;
};

/**
 * Gets truncated text for a given column (for external use)
 * 
 * @param {string} text - Text to truncate
 * @param {string} columnKey - Column key to get truncation limit from UIConfig
 * @returns {string} Truncated text with ellipsis or original text
 */
export const getTruncatedText = (text, columnKey) => {
  if (!text) return text;
  
  const lengthLimit = UIConfig.columnTextLimits[columnKey];
  const truncatedLimit = UIConfig.truncatedTextLimits[columnKey];
  
  if (!lengthLimit || !truncatedLimit || text.length <= lengthLimit) {
    return text;
  }
  
  return _getTruncatedText(text, columnKey);
};

/**
 * Checks if text should show tooltip based on column configuration
 * 
 * @param {string} text - Text to check
 * @param {string} columnKey - Column key to get length limit from UIConfig
 * @returns {boolean} True if tooltip should be shown
 */
export const shouldShowTooltip = (text, columnKey) => {
  return _shouldShowTooltip(text, columnKey);
};


/**
 * Helper function to render critical device list with tooltip and copy functionality
 * Uses UIConfig truncation limits similar to renderTextWithTooltip for consistent behavior
 * 
 * @param {string} criticalDeviceList - Comma-separated list of critical devices
 * @returns {React.ReactNode} Rendered critical device display with tooltip and copy
 */
export const renderCriticalDeviceList = (criticalDeviceDate, isFromSideDrawer) => {
  const criticalDeviceList = criticalDeviceDate.map(cd => cd.critical_id || cd.dual_critical_id).filter(id => id && id.trim() !== '').join(',') || [];

  if (!criticalDeviceList || criticalDeviceList === AppLabels.notApplicable || criticalDeviceList.length === 0) {
    return AppLabels.notApplicable;
  }
  const textValue = criticalDeviceList;
  const columnKey = 'criticalDeviceList';
  const lengthLimit = UIConfig.columnTextLimits[columnKey];
  const truncatedLimit = isFromSideDrawer? UIConfig.truncatedTextLimits[columnKey] *1.5 : UIConfig.truncatedTextLimits[columnKey];
  
  // Use UIConfig truncation logic similar to renderTextWithTooltip
  const shouldShowTooltip = lengthLimit && textValue.length > lengthLimit;
  const displayText = shouldShowTooltip && truncatedLimit 
    ? `${textValue.substring(0, truncatedLimit)}...`
    : textValue;
  
  if (shouldShowTooltip) {
    return (
      <PlugDrawerCriticalDeviceDisplay>
        <Tooltip 
          title={criticalDeviceList} 
          arrow 
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: '400px',
                fontSize: '0.875rem',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word'
              }
            }
          }}
        >
          <span style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: UIConfig.tooltipSettings.maxWidth
          }}>
            {displayText}
            <Info 
              className="info-icon"
              style={{ 
                fontSize: UIConfig.tooltipSettings.iconSize, 
                color: UIConfig.tooltipSettings.iconColor, 
                flexShrink: 0
              }} 
            />
          </span>
        </Tooltip>
      </PlugDrawerCriticalDeviceDisplay>
    );
  }
  
  // No truncation needed - show full text
  return (
    <PlugDrawerCriticalDeviceDisplay>
      {textValue}
    </PlugDrawerCriticalDeviceDisplay>
  );
};

/**
 * Helper function to render critical device list with dual supply identification in tooltip
 * Shows critical devices with single.png image and dual supply devices with dual.png image
 * Uses UIConfig truncation limits similar to renderTextWithTooltip for consistent behavior
 * 
 * @param {string} criticalDeviceList - Comma-separated list of all critical devices
 * @param {Array} criticalDevices - Array of actual critical devices from API
 * @param {Array} dualSupplyDevices - Array of dual supply devices
 * @returns {React.ReactNode} Rendered critical device display with enhanced tooltip using custom images
 */
export const renderCriticalDeviceListWithDualSupply = (criticalDeviceList, criticalDevices = [], dualSupplyDevices = []) => {
  if (!criticalDeviceList || criticalDeviceList === AppLabels.notApplicable) {
    return AppLabels.notApplicable;
  }
  
  const textValue = criticalDeviceList;
  const columnKey = 'criticalDeviceList';
  const lengthLimit = UIConfig.columnTextLimits[columnKey];
  
  // Adjust truncated limit based on dual supply devices presence to accommodate both icons
  let truncatedLimit = UIConfig.truncatedTextLimits[columnKey];
  if (dualSupplyDevices.length > 0 && truncatedLimit) {
    // Reduce by approximately 6-8 characters to make room for dual icon + info icon
    truncatedLimit = truncatedLimit - 2; // Ensure minimum of 10 characters
  }
  
  // Create enhanced tooltip content that differentiates between critical and dual supply devices
  const createTooltipContent = () => {
  const criticalIds = criticalDevices.map(cd => cd.critical_id).filter(id => id && id.trim() !== '');
  console.log('createTooltipContent', criticalIds);
    const dualSupplyIds = dualSupplyDevices.map(cd => cd.dual_critical_id).filter(id => id && id.trim() !== '');
    const isNullCriticalArray = criticalIds.length === 1 && criticalIds[0] === null;
     const isNullDualCriticalArray = dualSupplyIds.length === 1 && dualSupplyIds[0] === null;
    return (
      <div style={{ whiteSpace: 'pre-line', fontSize: '0.875rem', lineHeight: '1.4' }}>
        {!isNullCriticalArray && criticalIds.length > 0 && (
          <div>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Single Power Source Devices:</div>
            <div key={`critical`} style={{marginBottom: '2px', display: 'flex', alignItems: 'center' }}>
                <ElectricalServicesIcon 
              style={{ 
                fontSize: UIConfig.tooltipSettings.iconSize, 
                color: UIConfig.tooltipSettings.iconColor, 
                flexShrink: 0,
                marginRight: '6px',
              }} 
            />
              {criticalIds.join(', ')}
            </div>
          </div>
        )}
        
        {!isNullDualCriticalArray && dualSupplyIds.length > 0 && (
          <div style={{ marginTop: criticalIds.length > 0 ? '8px' : '0' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Dual Power Source Devices:</div>
            <div key={`dual-critical-devices`} style={{marginBottom: '2px', display: 'flex', alignItems: 'center' }}>
              <img 
                src="/dual.png" 
                alt="Dual Power Source"
                style={{ 
                  width: UIConfig.tooltipSettings.iconSize, 
                  height: UIConfig.tooltipSettings.iconSize,
                  flexShrink: 0,
                  marginRight: '6px',
                  backgroundColor: 'transparent'
                }} 
              /> 
              {dualSupplyIds.join(', ')}
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Use UIConfig truncation logic similar to renderTextWithTooltip
  const shouldShowTooltip = lengthLimit && textValue.length > lengthLimit;
  const displayText = shouldShowTooltip && truncatedLimit 
    ? `${textValue.substring(0, truncatedLimit)}...`
    : textValue;
  
  if (shouldShowTooltip) {
    return (
      <DashboardCopyableDisplay>
        <Tooltip 
          title={createTooltipContent()} 
          arrow 
          placement="top"
          slotProps={{
            tooltip: {
              sx: {
                maxWidth: '500px',
                fontSize: '0.875rem',
                padding: '12px',
                lineHeight: '1.4'
              }
            }
          }}
        >
          <span style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '4px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: UIConfig.tooltipSettings.maxWidth
          }}>
            {displayText}
            {dualSupplyDevices.length > 0 &&
              <img 
                src="/dual.png" 
                alt="Dual Power Source"
                style={{ 
                  width: UIConfig.tooltipSettings.iconSize, 
                  height: UIConfig.tooltipSettings.iconSize,
                  flexShrink: 0,
                  backgroundColor: 'transparent'
                }} 
              />
            }
            <Info 
              style={{ 
                fontSize: UIConfig.tooltipSettings.iconSize, 
                color: UIConfig.tooltipSettings.iconColor, 
                flexShrink: 0
              }} 
            />
          </span>
        </Tooltip>
      </DashboardCopyableDisplay>
    );
  }
  
  // No truncation needed - show full text with enhanced tooltip
  return (
    <DashboardCopyableDisplay>
      <Tooltip 
        title={createTooltipContent()} 
        arrow 
        placement="top"
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: '500px',
              fontSize: '0.875rem',
              padding: '12px',
              lineHeight: '1.4'
            }
          }
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          {textValue}
          {dualSupplyDevices.length > 0 &&
            <img 
              src="/dual.png" 
              alt="Dual Power Source"
              style={{ 
                width: UIConfig.tooltipSettings.iconSize, 
                height: UIConfig.tooltipSettings.iconSize,
                flexShrink: 0,
                backgroundColor: 'transparent'
              }} 
            />
          }
          <Info 
            style={{ 
              fontSize: UIConfig.tooltipSettings.iconSize, 
              color: UIConfig.tooltipSettings.iconColor, 
              flexShrink: 0
            }} 
          />
        </span>
      </Tooltip>
    </DashboardCopyableDisplay>
  );
};


/**
 * Format action text for better readability
 * @param {string} string or action - The string (e.g., "Modem", "modem", "REBOOT", "TURN_ON", "Pdu", "pdu")
 * @returns {string} Formatted action text (e.g., "Modem", "Modem", "Reboot", "Turn On", "PDU")
 */
export const formattedText = (action) => {
  if (!action) return AppLabels.notApplicable;

  // Special case for PDU - return "PDU" regardless of input case
  if (action.toLowerCase() === 'pdu') {
    return 'PDU';
  }

  // Convert to lowercase and split by underscore
  const words = action.toLowerCase().split('_');

  // Capitalize first letter of each word
  const formatted = words.map(word =>
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');

  return formatted;
};


/**
 * Default export for convenience
 */
const textTooltipUtils = {
  renderTextWithTooltip,
  getTruncatedText,
  shouldShowTooltip,
  renderCriticalDeviceList,
  renderCriticalDeviceListWithDualSupply,
  formattedText
};

export default textTooltipUtils;
